import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, Clock, FileText, Plus, Download, Trash2,
  Mail, Phone, Globe, Building, Edit, Save, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const getStatusBadgeClass = (status) => {
  if (status === "signed") return "bg-green-500/10 text-green-400";
  if (status === "sent") return "bg-blue-500/10 text-blue-400";
  return "bg-slate-700 text-slate-400";
};

const ClientDetail = () => {
  const { id } = useParams();
  useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showSOW, setShowSOW] = useState(false);
  const [sowForm, setSowForm] = useState({
    package_name: "", monthly_fee: "", initial_term: "6 Months",
    crm_platform: "GoHighLevel", campaign_count: "2", custom_scope: "",
  });
  const [previewSOW, setPreviewSOW] = useState(null);

  const fetchClient = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/portal/clients/${id}`, { withCredentials: true });
      setClient(data);
      setEditData({ name: data.name, company: data.company, email: data.email, phone: data.phone, website: data.website, industry: data.industry, notes: data.notes });
    } catch {
      toast.error("Failed to load client");
    }
    setLoading(false);
  }, [id, API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchClient(); }, [fetchClient]);

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/portal/clients/${id}`, editData, { withCredentials: true });
      toast.success("Client updated");
      setEditing(false);
      fetchClient();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleMSA = async () => {
    const newStatus = client.msa_status === "signed" ? "unsigned" : "signed";
    const date = newStatus === "signed" ? new Date().toISOString().split("T")[0] : null;
    try {
      await axios.put(`${API}/api/portal/clients/${id}`, { msa_status: newStatus, msa_signed_date: date }, { withCredentials: true });
      toast.success(`MSA marked as ${newStatus}`);
      fetchClient();
    } catch {
      toast.error("Failed to update MSA status");
    }
  };

  const handleCreateSOW = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/api/portal/sows`, { client_id: id, ...sowForm }, { withCredentials: true });
      toast.success(`SOW created: ${data.project_ref}`);
      setShowSOW(false);
      setSowForm({ package_name: "", monthly_fee: "", initial_term: "6 Months", crm_platform: "GoHighLevel", campaign_count: "2", custom_scope: "" });
      fetchClient();
      // Show preview
      const sowData = await axios.get(`${API}/api/portal/sows/${data.id}`, { withCredentials: true });
      setPreviewSOW(sowData.data);
    } catch {
      toast.error("Failed to create SOW");
    }
  };

  const handleDownloadPDF = (sowId) => {
    window.open(`${API}/api/portal/sows/${sowId}/pdf`, "_blank");
  };

  const handleDeleteSOW = async (sowId) => {
    try {
      await axios.delete(`${API}/api/portal/sows/${sowId}`, { withCredentials: true });
      toast.success("SOW deleted");
      fetchClient();
    } catch {
      toast.error("Failed to delete SOW");
    }
  };

  const handleDeleteClient = async () => {
    if (!window.confirm("Delete this client and all their SOWs?")) return;
    try {
      await axios.delete(`${API}/api/portal/clients/${id}`, { withCredentials: true });
      toast.success("Client deleted");
      window.location.href = "/portal";
    } catch {
      toast.error("Failed to delete client");
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-4 border-optima-blue border-t-transparent rounded-full animate-spin" /></div>;
  if (!client) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">Client not found</div>;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="client-detail">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/portal" className="text-slate-400 hover:text-white transition-colors" data-testid="back-to-portal">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-white font-bold text-lg">{client.name}</h1>
              <p className="text-slate-500 text-sm">{client.company}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} className="border-slate-700 text-slate-300" data-testid="edit-client-btn">
              {editing ? <><X className="w-4 h-4 mr-1" /> Cancel</> : <><Edit className="w-4 h-4 mr-1" /> Edit</>}
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteClient} data-testid="delete-client-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-8">
        {/* Left: Client Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Contact Info */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h2 className="text-white font-semibold mb-4">Client Information</h2>
            {editing ? (
              <div className="space-y-3">
                <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} placeholder="Name" className="bg-slate-800 border-slate-700 text-white" data-testid="edit-name" />
                <Input value={editData.company} onChange={(e) => setEditData({...editData, company: e.target.value})} placeholder="Company" className="bg-slate-800 border-slate-700 text-white" />
                <Input value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} placeholder="Email" className="bg-slate-800 border-slate-700 text-white" />
                <Input value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} placeholder="Phone" className="bg-slate-800 border-slate-700 text-white" />
                <Input value={editData.website} onChange={(e) => setEditData({...editData, website: e.target.value})} placeholder="Website" className="bg-slate-800 border-slate-700 text-white" />
                <Input value={editData.industry} onChange={(e) => setEditData({...editData, industry: e.target.value})} placeholder="Industry" className="bg-slate-800 border-slate-700 text-white" />
                <Textarea value={editData.notes} onChange={(e) => setEditData({...editData, notes: e.target.value})} placeholder="Notes" className="bg-slate-800 border-slate-700 text-white" />
                <Button onClick={handleUpdate} className="w-full bg-green-600 hover:bg-green-700" data-testid="save-edits-btn"><Save className="w-4 h-4 mr-2" /> Save Changes</Button>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-slate-300"><Mail className="w-4 h-4 text-slate-500" /> {client.email}</div>
                {client.phone && <div className="flex items-center gap-3 text-slate-300"><Phone className="w-4 h-4 text-slate-500" /> {client.phone}</div>}
                {client.website && <div className="flex items-center gap-3 text-slate-300"><Globe className="w-4 h-4 text-slate-500" /> {client.website}</div>}
                {client.industry && <div className="flex items-center gap-3 text-slate-300"><Building className="w-4 h-4 text-slate-500" /> {client.industry}</div>}
                {client.notes && <div className="mt-4 p-3 bg-slate-800 rounded-lg text-slate-400 text-xs">{client.notes}</div>}
              </div>
            )}
          </div>

          {/* MSA Status */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="msa-section">
            <h2 className="text-white font-semibold mb-4">Master Service Agreement</h2>
            <div className="flex items-center justify-between mb-4">
              <div>
                {client.msa_status === "signed" ? (
                  <span className="inline-flex items-center gap-2 text-green-400 font-medium"><CheckCircle className="w-5 h-5" /> Signed</span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-amber-400 font-medium"><Clock className="w-5 h-5" /> Pending Signature</span>
                )}
                {client.msa_signed_date && <p className="text-slate-500 text-xs mt-1">Signed: {client.msa_signed_date}</p>}
              </div>
            </div>
            <Button onClick={toggleMSA} variant="outline" size="sm" className="w-full border-slate-700 text-slate-300" data-testid="toggle-msa-btn">
              {client.msa_status === "signed" ? "Mark as Unsigned" : "Mark as Signed"}
            </Button>
          </div>
        </div>

        {/* Right: SOWs */}
        <div className="lg:col-span-2 space-y-6">
          {/* SOW Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold text-lg">Statements of Work</h2>
            <Button onClick={() => setShowSOW(true)} className="bg-optima-blue hover:bg-optima-blue/90" data-testid="new-sow-btn">
              <Plus className="w-4 h-4 mr-2" /> New SOW
            </Button>
          </div>

          {/* SOW List */}
          {client.sows && client.sows.length > 0 ? (
            <div className="space-y-4">
              {client.sows.map((sow) => (
                <div key={sow.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center justify-between" data-testid={`sow-card-${sow.id}`}>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <FileText className="w-5 h-5 text-optima-cyan" />
                      <span className="text-white font-medium">{sow.project_ref}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        getStatusBadgeClass(sow.status)
                      }`}>{sow.status}</span>
                    </div>
                    <p className="text-slate-500 text-sm">{sow.package_name} — {sow.monthly_fee}/mo</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(sow.id)} className="text-slate-400 hover:text-white" data-testid={`download-sow-${sow.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteSOW(sow.id)} className="text-slate-400 hover:text-red-400" data-testid={`delete-sow-${sow.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500">No SOWs created yet.</p>
              <p className="text-slate-600 text-sm mt-1">Create one to get started with this client's retainer.</p>
            </div>
          )}

          {/* SOW Preview */}
          {previewSOW && (
            <div className="bg-slate-900 border border-optima-cyan/30 rounded-xl p-6" data-testid="sow-preview">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">SOW Preview: {previewSOW.project_ref}</h3>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleDownloadPDF(previewSOW.id)} className="bg-optima-blue" data-testid="preview-download-pdf">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setPreviewSOW(null)} className="text-slate-400">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 text-slate-800 text-sm space-y-4 max-h-[500px] overflow-y-auto" data-testid="sow-preview-content">
                <p className="text-optima-blue font-bold text-xs uppercase tracking-wider">OptimaGrowth</p>
                <h4 className="text-lg font-bold text-slate-900">Statement of Work: Monthly Growth Retainer</h4>
                <p className="text-slate-500 text-xs">Project Ref: {previewSOW.project_ref} | Effective Date: {previewSOW.effective_date}</p>
                <p>This SOW is issued pursuant to the MSA between <strong>OptimaGrowth (Pty) Ltd</strong> and <strong>{previewSOW.client_name}</strong>.</p>
                <h5 className="font-bold text-slate-900 mt-4">1. Retainer Package & Fees</h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr><td className="font-medium pr-4 py-1">Package:</td><td>{previewSOW.package_name}</td></tr>
                    <tr><td className="font-medium pr-4 py-1">Monthly Fee:</td><td>{previewSOW.monthly_fee} (Excl. VAT)</td></tr>
                    <tr><td className="font-medium pr-4 py-1">Initial Term:</td><td>{previewSOW.initial_term}</td></tr>
                    <tr><td className="font-medium pr-4 py-1">Renewal:</td><td>Auto-renews month-to-month</td></tr>
                  </tbody>
                </table>
                <h5 className="font-bold text-slate-900">2. Payment Schedule</h5>
                <p>Invoiced 1st of each month. Net 15. Late interest at 2%/mo per the MSA.</p>
                <h5 className="font-bold text-slate-900">3. Monthly Scope</h5>
                <ul className="list-disc pl-5 space-y-1">
                  <li>CRM Management: Monthly audit of {previewSOW.crm_platform}</li>
                  <li>Automation Maintenance: 24/7 monitoring of workflows</li>
                  <li>Campaign Management: Up to {previewSOW.campaign_count} campaigns (Meta/Google)</li>
                  <li>Reporting: Monthly Growth Insights report</li>
                  <li>Strategy Call: One 45-min review session per month</li>
                </ul>
                {previewSOW.custom_scope && <p><strong>Additional:</strong> {previewSOW.custom_scope}</p>}
                <h5 className="font-bold text-slate-900">4. Client Responsibilities</h5>
                <p>Pre-approved ad budget (billed by platforms) and feedback on creative within 3 business days.</p>
                <h5 className="font-bold text-slate-900">5. Termination</h5>
                <p>30 days' written notice. Full final month payable if notice given mid-month.</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* New SOW Modal */}
      <Dialog open={showSOW} onOpenChange={setShowSOW}>
        <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="sow-modal">
          <DialogHeader>
            <DialogTitle>Create New SOW</DialogTitle>
            <DialogDescription className="text-slate-400">For {client.company || client.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSOW} className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Package Name *</label>
              <Input placeholder="e.g. Platinum Growth System" required value={sowForm.package_name} onChange={(e) => setSowForm({...sowForm, package_name: e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="sow-package" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Monthly Fee *</label>
                <Input placeholder="e.g. R15,000" required value={sowForm.monthly_fee} onChange={(e) => setSowForm({...sowForm, monthly_fee: e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="sow-fee" />
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Initial Term</label>
                <Input value={sowForm.initial_term} onChange={(e) => setSowForm({...sowForm, initial_term: e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="sow-term" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-300 mb-1 block">CRM Platform</label>
                <Input value={sowForm.crm_platform} onChange={(e) => setSowForm({...sowForm, crm_platform: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Campaign Count</label>
                <Input value={sowForm.campaign_count} onChange={(e) => setSowForm({...sowForm, campaign_count: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Additional Scope</label>
              <Textarea value={sowForm.custom_scope} onChange={(e) => setSowForm({...sowForm, custom_scope: e.target.value})} placeholder="Any extra scope items..." className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <Button type="submit" className="w-full bg-optima-blue" data-testid="create-sow-submit">Create SOW</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
